import { useState, useRef, useEffect } from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { DateRange } from "../../types/transactionHistory.types";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function getDefaultRange(): { startMonth: number; startYear: number; endMonth: number; endYear: number } {
  const now = new Date();
  return {
    startMonth: now.getMonth(),
    startYear: now.getFullYear(),
    endMonth: now.getMonth(),
    endYear: now.getFullYear(),
  };
}

function computeDateRange(
  startMonth: number,
  startYear: number,
  endMonth: number,
  endYear: number
): DateRange {
  const now = new Date();
  const startDate = `${startYear}-${String(startMonth + 1).padStart(2, "0")}-01`;

  const isCurrentMonth = endMonth === now.getMonth() && endYear === now.getFullYear();
  let endDay: number;
  if (isCurrentMonth) {
    endDay = now.getDate();
  } else {
    endDay = new Date(endYear, endMonth + 1, 0).getDate();
  }
  const endDate = `${endYear}-${String(endMonth + 1).padStart(2, "0")}-${String(endDay).padStart(2, "0")}`;

  return { startDate, endDate };
}

function formatRangeLabel(startMonth: number, startYear: number, endMonth: number, endYear: number): string {
  const now = new Date();
  const isCurrentMonth = endMonth === now.getMonth() && endYear === now.getFullYear();
  const endDay = isCurrentMonth ? now.getDate() : new Date(endYear, endMonth + 1, 0).getDate();

  if (startMonth === endMonth && startYear === endYear) {
    return `${MONTHS[startMonth]} 1 - ${MONTHS[endMonth]} ${endDay}, ${endYear}`;
  }
  if (startYear === endYear) {
    return `${MONTHS[startMonth]} - ${MONTHS[endMonth]} ${endYear}`;
  }
  return `${MONTHS[startMonth]} ${startYear} - ${MONTHS[endMonth]} ${endYear}`;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

export default function DateRangePicker({ value, onChange }: DateRangePickerProps) {
  const defaults = getDefaultRange();
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(defaults.endYear);
  const [startSel, setStartSel] = useState<{ month: number; year: number }>({
    month: defaults.startMonth,
    year: defaults.startYear,
  });
  const [endSel, setEndSel] = useState<{ month: number; year: number }>({
    month: defaults.endMonth,
    year: defaults.endYear,
  });
  const [selectingStart, setSelectingStart] = useState(true);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleMonthClick = (monthIdx: number) => {
    if (selectingStart) {
      setStartSel({ month: monthIdx, year });
      setEndSel({ month: monthIdx, year });
      setSelectingStart(false);
    } else {
      const startVal = startSel.year * 12 + startSel.month;
      const clickedVal = year * 12 + monthIdx;
      if (clickedVal >= startVal) {
        setEndSel({ month: monthIdx, year });
      } else {
        setStartSel({ month: monthIdx, year });
        setEndSel({ month: startSel.month, year: startSel.year });
      }
      setSelectingStart(true);
    }
  };

  const handleApply = () => {
    const range = computeDateRange(startSel.month, startSel.year, endSel.month, endSel.year);
    onChange(range);
    setOpen(false);
  };

  const isInRange = (monthIdx: number) => {
    const val = year * 12 + monthIdx;
    const sVal = startSel.year * 12 + startSel.month;
    const eVal = endSel.year * 12 + endSel.month;
    return val >= sVal && val <= eVal;
  };

  const isStart = (monthIdx: number) => monthIdx === startSel.month && year === startSel.year;
  const isEnd = (monthIdx: number) => monthIdx === endSel.month && year === endSel.year;

  const now = new Date();
  const isFutureMonth = (monthIdx: number) => year > now.getFullYear() || (year === now.getFullYear() && monthIdx > now.getMonth());

  const label = formatRangeLabel(startSel.month, startSel.year, endSel.month, endSel.year);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:border-gray-300 transition-colors"
      >
        <span className="text-gray-500 text-xs">📅</span>
        <span className="font-medium">{label}</span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-50 w-[320px] p-4 animate-fadeIn">
          {/* Year nav */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setYear((y) => y - 1)}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-gray-500" />
            </button>
            <span className="text-sm font-semibold text-gray-800">{year}</span>
            <button
              onClick={() => setYear((y) => (y < now.getFullYear() ? y + 1 : y))}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              disabled={year >= now.getFullYear()}
            >
              <ChevronRight className={`w-4 h-4 ${year >= now.getFullYear() ? "text-gray-300" : "text-gray-500"}`} />
            </button>
          </div>

          {/* Month grid */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            {MONTHS.map((m, idx) => {
              const future = isFutureMonth(idx);
              const inRange = isInRange(idx);
              const start = isStart(idx);
              const end = isEnd(idx);

              return (
                <button
                  key={m}
                  disabled={future}
                  onClick={() => handleMonthClick(idx)}
                  className={`py-2 px-1 text-xs font-medium rounded-lg transition-all
                    ${future ? "text-gray-300 cursor-not-allowed" : "cursor-pointer hover:bg-gray-50"}
                    ${start || end ? "bg-secondary text-white" : ""}
                    ${inRange && !start && !end ? "bg-red-50 text-secondary" : ""}
                    ${!inRange && !future ? "text-gray-600" : ""}
                  `}
                >
                  {m}
                </button>
              );
            })}
          </div>

          {/* Hint */}
          <p className="text-xs text-gray-400 mb-3 text-center">
            {selectingStart ? "Select start month" : "Select end month"}
          </p>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => setOpen(false)}
              className="flex-1 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="flex-1 py-2 text-xs font-medium text-white bg-secondary rounded-lg hover:opacity-90 transition-opacity"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
